import * as React from 'react'
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import { goHighLevelService } from '@/services/GoHighLevelService'
import { goHighLevelCompanyService } from '@/services/GoHighLevelCompanyService'
import { eventGoHighLevelService } from '@/services/EventGoHighLevelService'
import { ghlLeadsService } from '@/services/GhlLeadsService'
import { ghlBackfillService } from '@/services/GhlBackfillService'
import { eventKeys, ghlKeys, pipelineKeys } from './queryKeys'
import type { LeadStatsChoicesDto } from '@lib/dto/lead-stats.dto'
import type {
  EventGhlDto,
  GhlBackfillRunDto,
  GhlBackfillRunListDto,
  GhlReviewListDto,
  GhlAvailabilityDto,
  GhlCoupleListDto,
  GhlNameFormatDto,
  GhlCompanySettingsDto,
  GhlConnectionDto,
  GhlConnectionOptionsDto,
  GhlVenueSettingsDto,
} from '@lib/dto/gohighlevel.dto'
import type {
  CreateGhlConnectionInput,
  UpdateGhlConnectionInput,
  UpdateGhlNameFormatInput,
  PutEventGhlLinksInput,
  ResolveGhlReviewInput,
} from '@lib/input/gohighlevel.input'

/**
 * A venue's GoHighLevel page. `enabled` is false for a company without the
 * feature, which the API would refuse.
 */
export function useGhlSettings(venueId: string, enabled: boolean): UseQueryResult<GhlVenueSettingsDto> {
  return useQuery({
    queryKey: ghlKeys.settings(venueId),
    queryFn: () => goHighLevelService.settings(venueId),
    enabled: enabled && venueId !== '',
    // A failure here is usually GoHighLevel being off or unavailable; the page
    // says which. Retrying three times only delays telling the person.
    retry: false,
  })
}

/** What a connection's funnel mapping is chosen from: its last read, not GoHighLevel live. */
export function useGhlFunnelChoices(venueId: string, connectionId: string): UseQueryResult<LeadStatsChoicesDto> {
  return useQuery({
    queryKey: ghlKeys.funnelChoices(connectionId),
    queryFn: () => goHighLevelService.funnelChoices(venueId, connectionId),
    staleTime: 60_000,
  })
}

/** What GoHighLevel has for a connection now: its pipelines and contact fields. */
export function useGhlOptions(
  venueId: string,
  connectionId: string,
  enabled: boolean,
): UseQueryResult<GhlConnectionOptionsDto> {
  return useQuery({
    queryKey: ghlKeys.options(connectionId),
    queryFn: () => goHighLevelService.options(venueId, connectionId),
    enabled,
    retry: false,
    staleTime: 60_000,
  })
}

export function useCreateGhlConnection(
  venueId: string,
): UseMutationResult<GhlConnectionDto, Error, CreateGhlConnectionInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateGhlConnectionInput) => goHighLevelService.createConnection(venueId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ghlKeys.settings(venueId) })
      // Whether the venue has GoHighLevel at all decides what its pages show.
      void queryClient.invalidateQueries({ queryKey: ghlKeys.availability(venueId) })
    },
  })
}

interface UpdateConnectionVars {
  connectionId: string
  input: UpdateGhlConnectionInput
}

export function useUpdateGhlConnection(
  venueId: string,
): UseMutationResult<GhlConnectionDto, Error, UpdateConnectionVars> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ connectionId, input }: UpdateConnectionVars) =>
      goHighLevelService.updateConnection(venueId, connectionId, input),
    onSuccess: (_saved, { connectionId, input }) => {
      void queryClient.invalidateQueries({ queryKey: ghlKeys.settings(venueId) })
      // A new key may open what the old one couldn't.
      if (input.key !== undefined) void queryClient.invalidateQueries({ queryKey: ghlKeys.options(connectionId) })
      // The pipeline's stages are what the mapping names.
      if (input.funnelMap !== undefined) void queryClient.invalidateQueries({ queryKey: pipelineKeys.venue(venueId) })
    },
  })
}

export function useRemoveGhlConnection(venueId: string): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (connectionId: string) => goHighLevelService.removeConnection(venueId, connectionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ghlKeys.settings(venueId) })
      // Whether the venue has GoHighLevel at all decides what its pages show.
      void queryClient.invalidateQueries({ queryKey: ghlKeys.availability(venueId) })
    },
  })
}

/** The company's GoHighLevel page. `enabled` is false for a company without the feature. */
export function useGhlCompanySettings(enabled: boolean): UseQueryResult<GhlCompanySettingsDto> {
  return useQuery({
    queryKey: ghlKeys.company(),
    queryFn: () => goHighLevelCompanyService.settings(),
    enabled,
    retry: false,
  })
}

/** Sets the company's event name format, which every venue's page shows. */
export function useSetGhlNameFormat(): UseMutationResult<GhlCompanySettingsDto, Error, UpdateGhlNameFormatInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateGhlNameFormatInput) => goHighLevelCompanyService.setNameFormat(input),
    onSuccess: (settings) => {
      queryClient.setQueryData(ghlKeys.company(), settings)
      void queryClient.invalidateQueries({ queryKey: ghlKeys.venues() })
    },
  })
}

/** An event's GoHighLevel links and their history. `enabled` is false for a company without the feature. */
export function useEventGhl(eventId: string, enabled: boolean): UseQueryResult<EventGhlDto> {
  return useQuery({
    queryKey: ghlKeys.event(eventId),
    queryFn: () => eventGoHighLevelService.get(eventId),
    enabled,
    retry: false,
  })
}

/** Whether this venue's staff can be offered the lead picker. Asks GoHighLevel nothing. */
export function useGhlAvailability(venueId: string, enabled: boolean): UseQueryResult<GhlAvailabilityDto> {
  return useQuery({
    queryKey: ghlKeys.availability(venueId),
    queryFn: () => ghlLeadsService.availability(venueId),
    enabled: enabled && venueId !== '',
    retry: false,
  })
}

/** The couples the picker offers. The API keeps GoHighLevel's answers a minute; this half that. */
export function useGhlCouples(venueId: string, enabled: boolean): UseQueryResult<GhlCoupleListDto> {
  return useQuery({
    queryKey: ghlKeys.couples(venueId),
    queryFn: () => ghlLeadsService.couples(venueId),
    enabled: enabled && venueId !== '',
    staleTime: 30_000,
    retry: false,
  })
}

/** The company's event name format, which a picked couple's event is named in. */
export function useGhlNameFormat(venueId: string, enabled: boolean): UseQueryResult<GhlNameFormatDto> {
  return useQuery({
    queryKey: ghlKeys.nameFormat(venueId),
    queryFn: () => ghlLeadsService.nameFormat(venueId),
    enabled: enabled && venueId !== '',
    staleTime: 60_000,
    retry: false,
  })
}

/**
 * Link an event to a couple's opportunities, replace them, or unlink it. Its page,
 * its GoHighLevel section and the venue's picker all change with it.
 */
export function useSetEventGhlLinks(
  eventId: string,
  venueId: string,
): UseMutationResult<EventGhlDto, Error, PutEventGhlLinksInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PutEventGhlLinksInput) => eventGoHighLevelService.putLinks(eventId, input),
    onSuccess: (reply) => {
      queryClient.setQueryData(ghlKeys.event(eventId), reply)
      void queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) })
      void queryClient.invalidateQueries({ queryKey: ghlKeys.couples(venueId) })
    },
  })
}

/** A venue's recent backfill runs, newest first. */
export function useGhlBackfillRuns(venueId: string, enabled: boolean): UseQueryResult<GhlBackfillRunListDto> {
  return useQuery({
    queryKey: ghlKeys.runs(venueId),
    queryFn: () => ghlBackfillService.listRuns(venueId),
    enabled: enabled && venueId !== '',
    retry: false,
  })
}

const isUnderway = (status: string | undefined): boolean => status === 'queued' || status === 'running'

/**
 * One run, followed every second and a half while it is queued or running.
 * When it finishes, the review list, the picker and the run history are read
 * again: the run has changed all three.
 */
export function useGhlBackfillRun(venueId: string, runId: string | null): UseQueryResult<GhlBackfillRunDto> {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: ghlKeys.run(venueId, runId ?? ''),
    queryFn: () => ghlBackfillService.getRun(venueId, runId ?? ''),
    enabled: runId !== null,
    refetchInterval: (q) => (isUnderway(q.state.data?.status) ? 1500 : false),
  })
  const status = query.data?.status
  const seenUnderway = React.useRef(false)
  React.useEffect(() => {
    if (isUnderway(status)) {
      seenUnderway.current = true
      return
    }
    if (status === undefined || !seenUnderway.current) return
    seenUnderway.current = false
    void queryClient.invalidateQueries({ queryKey: ghlKeys.review(venueId) })
    void queryClient.invalidateQueries({ queryKey: ghlKeys.couples(venueId) })
    void queryClient.invalidateQueries({ queryKey: ghlKeys.runs(venueId) })
  }, [status, venueId, queryClient])
  return query
}

export function useStartGhlBackfill(venueId: string): UseMutationResult<GhlBackfillRunDto, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => ghlBackfillService.start(venueId),
    onSuccess: (run) => {
      queryClient.setQueryData(ghlKeys.run(venueId, run.id), run)
      void queryClient.invalidateQueries({ queryKey: ghlKeys.runs(venueId) })
    },
  })
}

/** The events the backfill left for an organization admin to decide. */
export function useGhlReview(venueId: string, enabled: boolean): UseQueryResult<GhlReviewListDto> {
  return useQuery({
    queryKey: ghlKeys.review(venueId),
    queryFn: () => ghlBackfillService.review(venueId),
    enabled: enabled && venueId !== '',
    retry: false,
  })
}

/** Settle a review item: link to a couple, or none. The list and the venue's picker change with it. */
export function useResolveGhlReview(
  venueId: string,
): UseMutationResult<void, Error, { itemId: string; input: ResolveGhlReviewInput | null }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: ResolveGhlReviewInput | null }) =>
      input === null ? ghlBackfillService.reject(venueId, itemId) : ghlBackfillService.confirm(venueId, itemId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ghlKeys.review(venueId) })
      void queryClient.invalidateQueries({ queryKey: ghlKeys.couples(venueId) })
    },
  })
}
